/**
 * Il raccoglitore: i documenti messi da parte girando per l'app.
 *
 * Si gira fra fascicoli, cantieri, mezzi e documenti aziendali, si mette da
 * parte quello che serve, e alla fine esce una cartella sola. La lista vive
 * qui, nel browser di chi sta lavorando: al server si manda solo al momento
 * di scaricare, e li' ogni pezzo viene ricontrollato.
 *
 * Sta nella `sessionStorage`, non nella `localStorage`: e' un banco di lavoro,
 * non un archivio. Chiusa la scheda o fatta la disconnessione si svuota da
 * solo, ed e' giusto cosi' - dentro ci passano anche documenti personali.
 */
import { useSyncExternalStore } from "react";

const CHIAVE = "sgm_raccoglitore";

const leggiDaSessione = () => {
  try {
    const t = window.sessionStorage.getItem(CHIAVE);
    const v = t ? JSON.parse(t) : [];
    return Array.isArray(v) ? v : [];
  } catch { return []; }
};

let voci = leggiDaSessione();
const ascoltatori = new Set();

const scrivi = (nuove) => {
  voci = nuove;
  try { window.sessionStorage.setItem(CHIAVE, JSON.stringify(voci)); } catch { /* sessione negata: pazienza */ }
  ascoltatori.forEach((f) => f());
};

/** Due documenti sono lo stesso documento se hanno tipo e id uguali. */
export const chiaveVoce = (v) => `${v.tipo}:${v.id}`;

export const vociRaccoglitore = () => voci;

/**
 * Mette dentro una voce o un elenco di voci, saltando quelle che ci sono già.
 * Torna quante ne sono entrate davvero, che è quello che si dice a chi guarda.
 */
export function aggiungiAlRaccoglitore(nuove) {
  const elenco = Array.isArray(nuove) ? nuove : [nuove];
  const dentro = new Set(voci.map(chiaveVoce));
  const buone = elenco.filter((v) => v && v.tipo && v.id && !dentro.has(chiaveVoce(v)));
  if (buone.length) scrivi([...voci, ...buone.map((v) => ({ ...v }))]);
  return { aggiunte: buone.length, gia: elenco.length - buone.length };
}

export function togliDalRaccoglitore(chiavi) {
  const via = new Set(Array.isArray(chiavi) ? chiavi : [chiavi]);
  scrivi(voci.filter((v) => !via.has(chiaveVoce(v))));
}

export function svuotaRaccoglitore() {
  if (voci.length) scrivi([]);
  else { try { window.sessionStorage.removeItem(CHIAVE); } catch { /* niente */ } }
}

export const nelRaccoglitore = (voce) => {
  const k = chiaveVoce(voce);
  return voci.some((v) => chiaveVoce(v) === k);
};

const iscrivi = (f) => { ascoltatori.add(f); return () => ascoltatori.delete(f); };

/**
 * Le voci di adesso, con il componente che si ridisegna quando cambiano.
 *
 * Si chiama `use...` e non `usa...`: il controllo di React riconosce un hook
 * dal nome, e con il nome italiano il programma non si compila nemmeno.
 */
export function useRaccoglitore() {
  return useSyncExternalStore(iscrivi, vociRaccoglitore, vociRaccoglitore);
}
